/* eslint-disable react/prop-types */
import { getMatchingVariants } from '../../utils/variant';
import { BodyCopy } from '../ui/BodyCopy/BodyCopy';
import CartItem from './CartItem/CartItem';

const mapItem = (imageBaseURL, item) => {
  const { brand, displayName, id, prices } = item.product;

  return {
    brandName: brand,
    name: displayName,
    skuId: id,
    image: `${imageBaseURL}/${id}`,
    productId: '',
    price: prices,
    totalQuantity: 0,
  };
};

const mapBundledItem =
  ({ bundleItems, setBusy, removeProduct, setWarrantyOption, addProduct, imageBaseURL, limitAlerts }) =>
  (item) => {
    let isItemInCart = false;
    let bundleItem;

    bundleItems.forEach((bundle) => {
      const itemId = item?.product?.id ?? item?.id ?? '';
      if (!isItemInCart && bundle.skuId === itemId) {
        isItemInCart = true;
        bundleItem = bundle;
      }
    });

    const { skuId, id } = item;

    return (
      <CartItem
        key={skuId || id}
        setBusy={setBusy}
        item={isItemInCart ? bundleItem : mapItem(imageBaseURL, item)}
        onRemoveFromCart={removeProduct}
        showWarrantyOption={false}
        showAssemblyOption={false}
        setWarrantyOption={setWarrantyOption}
        onAddToCart={addProduct}
        type="bundle"
        limitAlerts={limitAlerts}
      />
    );
  };

const renderAddedBundle = ({
  labels,
  imageBaseURL,
  CartAddedBundleStyles,
  renderDescription,
  renderBrandName,
  bundleItems,
}) => {
  return (
    <>
      <div className="added-bundles">
        <div className="header mkp">
          <i className="csicon-icon-success-circle">
            <span className="sr-only">{labels.ITEM_ADDED_A11Y_LABEL}</span>
          </i>

          <span className="label">
            {bundleItems.length} {labels.ITEM_ADDED_IN_CART}
          </span>
        </div>

        <div className="bundle-items">
          {bundleItems.map(({ brandName, name, skuId }) => (
            <div className="bundle-item">
              <img src={`${imageBaseURL}/${skuId}`} width="50" height="50" alt={name} />
              <div className="item-info">
                {renderBrandName(brandName)}
                {renderDescription(name)}
              </div>
            </div>
          ))}
        </div>
      </div>
      <style jsx>{CartAddedBundleStyles}</style>
    </>
  );
};

const renderBundleItem = ({
  bundledProductOrigin,
  bundles,
  labels,
  imageBaseURL,
  CartAddedBundleStyles,
  bundleItems,
  setBusy,
  removeProduct,
  setWarrantyOption,
  addProduct,
  renderDescription,
  renderBrandName,
  isTypeA,
  limitAlerts,
}) => {
  if (isTypeA || !bundledProductOrigin) {
    return null;
  }

  if (bundledProductOrigin === 'CART') {
    return renderAddedBundle({
      labels,
      bundles,
      imageBaseURL,
      CartAddedBundleStyles,
      renderDescription,
      renderBrandName,
      bundleItems,
    });
  }

  const renderBundledItem = mapBundledItem({
    bundleItems,
    setBusy,
    removeProduct,
    setWarrantyOption,
    addProduct,
    imageBaseURL,
    limitAlerts,
  });
  return bundles.map(renderBundledItem);
};

const getTypeCollectionFromCart = (collection, variantId) => {
  for (let i = 0; i < collection.length; i += 1) {
    if (collection[i].skuId === variantId || collection[i].productId === variantId) {
      return collection[i];
    }
  }
  return null;
};

const isCollectionInMainSection = (cartItems, variantId) => {
  for (let i = 0; i < cartItems.length; i += 1) {
    if (cartItems[i].skuId === variantId) {
      return true;
    }
  }
  return false;
};

const renderCollections = ({
  labels,
  variants,
  colorCode,
  brandName,
  imageBaseURL,
  productId,
  setBusy,
  removeProduct,
  addProduct,
  cartItems,
  collection,
  limitAlerts,
}) => {
  const variantsToRender = getMatchingVariants(variants, {
    colorCode,
  }).filter((variant) => {
    const { id: skuId } = variant;

    return !isCollectionInMainSection(cartItems, skuId);
  });

  return (
    variantsToRender &&
    variantsToRender.length !== 0 && (
      <>
        <BodyCopy size="title14">{labels.MORE_PRODUCTS_FROM_COLLECTION}</BodyCopy>
        {variantsToRender.map((variant) => {
          const { name, id: skuId } = variant;

          const collectionItem = getTypeCollectionFromCart(collection, skuId);

          const item = {
            brandName,
            name,
            skuId,
            image: `${imageBaseURL}/${skuId}`,
            productId,
            price: variant.prices,
            totalQuantity: collectionItem?.totalQuantity ?? -1,
            commerceItemId: collectionItem?.commerceItemId ?? 0,
            addedQuantity: collectionItem?.addedQuantity ?? 0,
            cartLineId: collectionItem?.cartLineId ?? '',
            offeringId: variant?.offerings?.[0]?.offeringId ?? '',
          };

          return (
            <CartItem
              key={skuId}
              setBusy={setBusy}
              item={item}
              onRemoveFromCart={removeProduct}
              showWarrantyOption={false}
              showAssemblyOption={false}
              onAddToCart={addProduct}
              limitAlerts={limitAlerts}
            />
          );
        })}
      </>
    )
  );
};

const showWarningMessage = (stockUnits, cart) => {
  const { cartItems = [], bundleItems = [], typeACollections = [] } = cart;

  const products = [...cartItems, ...bundleItems, ...typeACollections];

  return !!products
    .filter((p) => p)
    .find((p) => {
      if (p.skuId && typeof stockUnits[p.skuId] !== 'undefined' && stockUnits[p.skuId] <= 3) {
        return true;
      }
      return false;
    });
};

let lastWarrantyTrackingXLP = {
  time: 0,
  source: '',
  trackingValue: '',
};

const trackWarrantySelectionXLP = (productSku, warrantyName, warrantySku) => {
  const trackingValue = `|Warranty!XLP!${productSku}!${warrantyName}!${warrantySku}|`;
  const now = Date.now();
  const isRecentEvent = now - lastWarrantyTrackingXLP.time < 2000;
  const isSameWarranty = lastWarrantyTrackingXLP.trackingValue.includes(
    `!${productSku}!${warrantyName}!${warrantySku}`
  );

  let shouldTrack = true;
  if (isRecentEvent && isSameWarranty) {
    shouldTrack = false;
  }

  if (!shouldTrack) return;

  lastWarrantyTrackingXLP = {
    time: now,
    source: 'XLP',
    trackingValue,
  };

  if (window?.digitalData?.tag?.XLP) {
    window.digitalData.tag.XLP.warranty_tracking = trackingValue;
  }

  const eventToDigitalData = new CustomEvent('DDXLPWarrantySelection', {
    bubbles: true,
    detail: {
      payload: trackingValue,
    },
  });

  window.dispatchEvent(eventToDigitalData);
};

const trackWarrantyRejectionXLP = (productSku) => {
  trackWarrantySelectionXLP(productSku, 'Continuar sin protección', '');
};

const resetWarrantyTrackingXLP = () => {
  const resetEvent = new CustomEvent('DDXLPWarrantySelectionUnset', {
    bubbles: true,
  });
  window.dispatchEvent(resetEvent);
  if (window?.digitalData?.tag?.XLP) {
    delete window.digitalData.tag.XLP.warranty_tracking;
  }
};

export {
  renderBundleItem,
  renderCollections,
  resetWarrantyTrackingXLP,
  showWarningMessage,
  trackWarrantyRejectionXLP,
  trackWarrantySelectionXLP,
};
